/**
 * Column definitions for the Triaxial Result-Entry grids (Consolidation +
 * Shear). Kept as data objects (per CODING-STANDARDS) so the grid headers can
 * render a formula tooltip on hover — see FEAT-2026-0009.
 *
 * The formula / note text is NO LONGER transcribed here. Each column carries a
 * `columnKey`; the header pulls `formula`/`note` from the backend Formula
 * Registry via `useTestFormulas('triaxial')`, so the tooltip can never drift
 * from the real calc (FEAT "Formula Registry", 2026-07-15). The registry is the
 * source of truth — backend `calculation/formulas/triaxialFormulas.ts`, golden-
 * verified against `computeConsolidationRow` / `computeShearRow`.
 *
 * Keys are namespaced (`cons_*` / `shear_*`) because both grids share the one
 * `triaxial` test type and reuse labels like "Stage" and "Axial Displ (mm)".
 */
import type { TriaxialTestType } from '../../../types/triaxial';

export interface TriaxialResultColumn {
  /** Registry key for the header formula tooltip (matches the backend entry). */
  columnKey: string;
  /** Header text shown in the grid (units / symbols included). */
  label: string;
  /** Whether the cell value is measured (raw GDS) or computed. */
  kind: 'raw' | 'calc';
}

// ── Consolidation grid ──────────────────────────────────────────────────────
// Render order matches TriaxialResultEntry.tsx consolidation <thead>.
export const consolidationColumns: TriaxialResultColumn[] = [
  { columnKey: 'cons_stage', label: 'Stage', kind: 'raw' },
  { columnKey: 'cons_originalTime', label: 'Original Time (s)', kind: 'raw' },
  { columnKey: 'cons_axialDisplRaw', label: 'Axial Displ raw (mm)', kind: 'raw' },
  { columnKey: 'cons_backVolumeRaw', label: 'Back Volume raw (mm³)', kind: 'raw' },
  { columnKey: 'cons_consTime', label: 'Cons. Time (s)', kind: 'calc' },
  { columnKey: 'cons_corrBackVol', label: 'Corr. Back Vol (mm³)', kind: 'calc' },
  { columnKey: 'cons_axialDispl', label: 'Axial Displ (mm)', kind: 'calc' },
];

// ── Shear grid ──────────────────────────────────────────────────────────────
// Built per test type: ΔV appears only for CID (drained), Δu only for CIU
// (undrained). Order matches TriaxialResultEntry.tsx shear <thead>.
export function buildShearColumns(testType: TriaxialTestType): TriaxialResultColumn[] {
  const cols: TriaxialResultColumn[] = [
    { columnKey: 'shear_stage', label: 'Stage', kind: 'raw' },
    { columnKey: 'shear_time', label: 'Time (s)', kind: 'raw' },
    { columnKey: 'shear_porePress', label: 'Pore Press (kPa)', kind: 'raw' },
    { columnKey: 'shear_axialDisplRaw', label: 'Axial Displ raw (mm)', kind: 'raw' },
    { columnKey: 'shear_epsA', label: 'ε_a (%)', kind: 'calc' },
    { columnKey: 'shear_axialDispl', label: 'Axial Displ (mm)', kind: 'calc' },
    { columnKey: 'shear_loadCell', label: 'Load Cell (kN)', kind: 'raw' },
    { columnKey: 'shear_effSigma3', label: "σ'₃ (kPa)", kind: 'calc' },
  ];

  if (testType === 'CID') {
    cols.push({ columnKey: 'shear_deltaV', label: 'ΔV (mm³)', kind: 'calc' });
  }

  cols.push(
    { columnKey: 'shear_q', label: 'q (kPa)', kind: 'calc' },
    { columnKey: 'shear_p', label: 'p (kPa)', kind: 'calc' },
    { columnKey: 'shear_pPrime', label: "p' (kPa)", kind: 'calc' },
    { columnKey: 'shear_t', label: 't (kPa)', kind: 'calc' },
    { columnKey: 'shear_s', label: "s' (kPa)", kind: 'calc' },
    { columnKey: 'shear_stressRatio', label: "σ'₁/σ'₃", kind: 'calc' },
  );

  if (testType === 'CIU') {
    cols.push({ columnKey: 'shear_deltaU', label: 'Δu (kPa)', kind: 'calc' });
  }

  return cols;
}
